<template>
  <div>
    <b-overlay
      :show="!dataIsReady"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <failed-data-get
        v-if="failedGetResellerStatus || failedIntegrationGetStatus"
        text="Looks like this reseller doesn't exist." />

      <template v-else>
        <div class="d-flex align-items-end pt-2 pb-6">
          <div class="d-flex flex-column">
            <h1 class="h5 mb-2">
              Webhook Integration
            </h1>
            <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
              <b-breadcrumb-item :to="{ name: 'Dashboard' }">
                <i class="fa fa-home fa-sm mr-2"></i>
                Home
              </b-breadcrumb-item>
              <b-breadcrumb-item :to="{ name: 'ResellersList' }">Resellers</b-breadcrumb-item>
              <b-breadcrumb-item :to="{ name: 'ResellerIntegrations', params: { id: routeId } }">Intergations</b-breadcrumb-item>
              <b-breadcrumb-item active>Webhook</b-breadcrumb-item>
            </b-breadcrumb>
          </div>
        </div>

        <b-row>
          <b-col
            xl="9"
            class="pr-4 pr-xl-16">
            <b-card class="card-custom card-border mb-4 mb-lg-8">
              <b-row>
                <b-col xl="8">
                  <!-- Wrapper for fake element + checkbox -->
                  <div
                    class="position-relative"
                    style="width: fit-content;">
                    <!-- Fake element above checkbox that TRIGGERS event -->
                    <div
                      class="w-100 h-100 position-absolute"
                      style="z-index: 20;"
                      role="button"
                      @click="handlewebhookIsEnabledChange" />
                    <!-- Checkbox that REPRESENTS current status, not changes it -->
                    <b-form-checkbox
                      :checked="webhookIsEnabled"
                      size="lg"
                      switch
                      class="switch-primary">
                      <span class="font-size-lg">Enable webhook</span>
                    </b-form-checkbox>
                  </div>

                  <template v-if="webhookIsEnabled">
                    <div class="py-8">
                      <b-form
                        inline
                        class="w-100 mb-1  align-items-start">
                        <b-form-group
                          label="Webhook URL"
                          class="flex-grow-1">
                          <b-form-input
                            id="input-webhook-url"
                            v-model="webhookUrl"
                            :disabled="webhookResponses('updateWebhookUrl')"
                            :state="webhookUrlErrors.length > 0 ? false : null"
                            class="flex-grow-1 w-100 mr-auto"
                            placeholder="https://webhook.site/e92c9806-aaba-45b2-91e2-a8fc35e2f509"
                            @input="clearWebhookUrlErrors" />
                          <b-form-invalid-feedback
                            v-for="(error, index) in webhookUrlErrors"
                            :key="`${error.field}-${index}`">
                            {{ error.message }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                        <b-button
                          v-wave
                          variant="primary"
                          class="ml-2 mt-8"
                          :disabled="webhookResponses('updateWebhookUrl')"
                          @click="clearWebhookUrlErrors(), updateWebhookUrl()">
                          <span>Save</span>
                        </b-button>
                      </b-form>
                    </div>
                    
                    <template v-if="webhookJobsCount > 0">
                      <h6 class="mb-0">
                        Messages Queue
                        <b-button
                          variant="link-primary"
                          size="sm"
                          class="btn-icon"
                          :disabled="webhookResponses('getWebhookJobs')"
                          @click="getWebhookJobs()">
                          <i class="fa fa-redo fa-sm m-0"></i>
                        </b-button>
                      </h6>
                      <div class="text-dark-50 mb-6">
                        <span class="font-weight-bold">{{ webhookJobsCount }}</span> {{ 'message' | pluralize(webhookJobsCount) }} waiting to be sent
                      </div>
                      <ul class="p-0 mb-6">
                        <webhook-job-item
                          v-for="job in webhookJobs"
                          :key="`job-${job.time}`"
                          :job="job" />
                      </ul>
                    </template>
                    <no-data-found
                      v-else
                      text="No messages yet"
                      class="mt-7" />
                  </template>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
          <b-col xl="3">
            <b-card
              class="card-custom card-border"
              body-class="pb-lg-4">
              <reseller-details-plate :flush="true" />
            </b-card>
          </b-col>
        </b-row>
      </template>
    </b-overlay>

    <modal-disable-webhook />
  </div>
</template>

<script>
import {
  // actions
  GET_RESELLER,
  
  // mutations
  SELECT_RESELLER,
  UNSELECT_RESELLER
} from '@/core/services/store/resellers.module'
import {
  // actions
  GET_WEBHOOK_DATA,
  UPDATE_WEBHOOK_URL,
  GET_WEBHOOK_JOBS,

  // mutations
  SAVE_WEBHOOK_URL,
  SAVE_WEBHOOK_STATUS,
  CLEAR_WEBHOOK_URL_ERRORS
} from '@/core/services/store/reseller_webhook.module'
import { GET_INTEGRATIONS } from '@/core/services/store/integrations.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import { showToast, focusInput } from '@/core/utils'
import ResellerDetailsPlate from './components/ResellerDetailsPlate.vue'
import ModalDisableWebhook from './components/ModalDisableWebhook.vue'
import WebhookJobItem from './components/WebhookJobItem.vue'

export default {
  name: 'ResellerWebhook',
  components: {
    ResellerDetailsPlate,
    ModalDisableWebhook,
    WebhookJobItem,
  },
  data() {
    return {
      dataIsReady: false,

      showToast,
      focusInput
    }
  },
  computed: {
    ...mapGetters([
      'integrationsList',
      'failedGetResellerStatus',
      'failedIntegrationGetStatus',
      'webhookUrlLength',
      'webhookUrlEmpty',
      'webhookUrlErrors',
      'webhookUrlError',
      'webhookJobsCount',
      'webhookJobs',
      'webhookApiKey',
      'webhookApiKeyExist',
      'webhookIpLimitsLength',
      'webhookIpLimitsEmpty',
      'webhookIpLimitsChanged',
      'webhookIpLimitsSnapshot',
      'webhookJobsVocabulary',
      'webhookJobTitle',
      'webhookResponses'
    ]),

    webhookUrl: {
      get() {
        return this.$store.getters['webhookUrl']
      },
      set(newVal) {
        this.$store.commit(SAVE_WEBHOOK_URL, newVal)
      }
    },

    webhookIsEnabled: {
      get() {
        return this.$store.getters['webhookIsEnabled']
      },
      set(newVal) {
        this.$store.commit(SAVE_WEBHOOK_STATUS, newVal)
      }
    },

    routeId() {
      return this.$route.params.id
    },
  },
  watch: {
    /* eslint-disable-next-line no-unused-vars */
    routeId(newVal, oldVal) {
      if (newVal) {
        this.dataIsReady = false
        this.$store.commit(UNSELECT_RESELLER)
        this.getData(newVal)
      }
    },
  },
  created() {
    this.getData(this.routeId)

    eventBus.$on('showToast/ResellerWebhook', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  beforeDestroy() {
    this.dataIsReady = false
  },
  destroyed() {
    eventBus.$off('showToast/ResellerWebhook')

    this.$store.commit(UNSELECT_RESELLER)
  },
  methods: {
    getData(id) {
      let requirements = [
        () => this.$store.dispatch(GET_RESELLER, { id })
          .then(data => { this.selectReseller(data) }),
        () => this.$store.dispatch(GET_WEBHOOK_DATA, { id }),
        () => this.$store.dispatch(GET_WEBHOOK_JOBS, { id, method: 'start' }),
        () => this.$store.dispatch(GET_INTEGRATIONS, { id })
      ]

      Promise
        .all(requirements.map(promiseFn => promiseFn()))
        .finally(() => this.dataIsReady = true)
    },

    selectReseller(reseller) {
      this.$store.commit(SELECT_RESELLER, reseller)
    },

    handlewebhookIsEnabledChange() {
      if (this.webhookUrlEmpty && this.webhookIsEnabled && this.webhookJobsCount == 0) {
        this.webhookIsEnabled = false
        this.clearWebhookUrlErrors()
      } else if (!this.webhookIsEnabled) {
        this.webhookIsEnabled = true
        this.focusInput('webhook-url')
      } else if (this.webhookJobsCount > 0 && this.webhookIsEnabled) {
        eventBus.$emit('openModal', 'confirmWebhookDisable')
      }
    },

    updateWebhookUrl() {
      this.$store
        .dispatch(UPDATE_WEBHOOK_URL, { id: this.routeId })
        .then(() => {
          this.getWebhookData()
          this.getWebhookJobs()
        })
    },

    getWebhookData() {
      this.$store.dispatch(GET_WEBHOOK_DATA, { id: this.routeId })
    },

    getWebhookJobs() {
      this.$store.dispatch(GET_WEBHOOK_JOBS, {
        id: this.routeId,
        method: 'start'
      })
    },

    clearWebhookUrlErrors() {
      this.$store.commit(CLEAR_WEBHOOK_URL_ERRORS)
    }
  }
}
</script>
